import {
    DEFAULT_WARDENCLYFFE_STORY,
    DEFAULT_WARDENCLYFFE_VIDEO,
} from "~mocks/mockStoryBuilder";
import type {
    Image as HydratedImage,
    Video as HydratedVideo,
} from "~types/types";
import {
    type Newsletter as WardenclyffeNewsletter,
    type Person as WardenclyffePerson,
    type Image as WardenclyffeImage,
    type Audio as WardenclyffeAudio,
    AspectRatio as WardenclyffeAspectRatio,
} from "~types/wardenclyffe";

import {
    type MediaConfig,
    type HydratedMediaCarouselData,
    type HydratedVodVideoData,
    MediaFormat,
} from "./types";

export const HIDE_CREDIT_SECRET_STRING = "hide";
export const LOOPING_VIDEO_KEYWORD = "edt-ambient";
export const DEFAULT_LIVESTREAM = "US";
export const SQUARE_NEWSLETTER_IMAGE_PLACEMENT = "card.mobile";
export const VERTICAL_VIDEO_NICODE = "VIDPORT";
export const FIVE_SECONDS_IN_MILLISECONDS = 1000 * 5;

export const DEFAULT_MEDIA_CONFIG: MediaConfig = {
    mobileMaxWidth: 715,
    showCaption: true,
    showPlayIcon: false,
    useImageLink: true,
};

export const DEFAULT_MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.snapshotChart,
    MediaFormat.embeddedIframe,
    MediaFormat.loopingVideo,
    MediaFormat.vodVideo,
    MediaFormat.livestreamVideo,
    MediaFormat.defaultImage,
];

export const IMAGE_MEDIA_FORMATS = new Set<MediaFormat>([
    MediaFormat.defaultImage,
    MediaFormat.audioImage,
    MediaFormat.authorImage,
    MediaFormat.magazineCoverImage,
    MediaFormat.newsletterImage,
    MediaFormat.videoImage,
]);

export const VIDEO_MEDIA_FORMATS = new Set<MediaFormat>([
    MediaFormat.vodVideo,
    MediaFormat.livestreamVideo,
    MediaFormat.loopingVideo,
    MediaFormat.verticalVideo,
]);

export const ALL_MEDIA_FORMATS = new Set<MediaFormat>([
    ...VIDEO_MEDIA_FORMATS,
    ...IMAGE_MEDIA_FORMATS,
    MediaFormat.carousel,
    MediaFormat.embeddedIframe,
    MediaFormat.snapshotChart,
]);

export const CAROUSEL_ITEM_TYPES = {
    image: "image",
    loopingVideo: "loopingVideo",
};

export const MOCK_HYDRATED_IMAGE = {
    alt: "Alt text for image",
    baseUrl:
        "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/iDYwnS57AkrI/v0/-1x-1.jpg",
    caption: "",
    credit: "Credit from image",
    origWidth: 1000,
    origHeight: 1000,
    placement: "",
    themes: [],
    type: "image",
} as HydratedImage;

export const MOCK_HYDRATED_VIDEO = {
    autoplay: false,
    bmmrId: "0fb39f8-47a4-46da-92d7-c014303bdbf4",
    prerollAdUnit: "",
    url: "http://www.bloomberg.com/video.mp4",
    thumbnail: MOCK_HYDRATED_IMAGE,
    title: "Title from associated video",
    duration: 1280821,
    type: "Video",
    keywords: [],
    showName: "",
    aspectRatio: "16:9",
    isVertical: false,
    isLooping: false,
    hideCaptionByRequest: false,
    isVideoCurationOverride: false,
} as HydratedVideo;

export const MOCK_HYDRATED_VIDEO_WITHOUT_CAPTION_LOGIC = {
    autoplay: false,
    bmmrId: "0fb39f8-47a4-46da-92d7-c014303bdbf4",
    prerollAdUnit: "",
    url: "http://www.bloomberg.com/video.mp4",
    thumbnail: MOCK_HYDRATED_IMAGE,
    title: "Title from associated video",
    duration: 1280821,
    type: "Video",
    keywords: [],
    showName: "",
    aspectRatio: "16:9",
    isVertical: false,
    isLooping: false,
    hideCaptionByRequest: false,
    isVideoCurationOverride: false,
} as HydratedVideo;

export const MOCK_HYDRATED_LOOPING_VIDEO = {
    ...MOCK_HYDRATED_VIDEO,
    autoplay: true,
    isLooping: true,
    isVideoCurationOverride: true,
    keywords: [LOOPING_VIDEO_KEYWORD],
};

export const MOCK_HYDRATED_VERTICAL_VIDEO = {
    ...MOCK_HYDRATED_VIDEO,
    aspectRatio: "2:3",
    autoplay: false,
    isVertical: true,
    isVideoCurationOverride: false,
};

export const MOCK_HYDRATED_VERTICAL_VIDEO_DATA_WITH_ORIGINAL_THUMBNAIL = {
    video: {
        ...MOCK_HYDRATED_VERTICAL_VIDEO,
        thumbnail: {
            ...MOCK_HYDRATED_IMAGE,
            baseUrl: "www.test-base-url-video-thumbnail-1",
        },
    } as HydratedVideo,
    image: {
        ...MOCK_HYDRATED_IMAGE,
        baseUrl: "www.test-base-url-video-thumbnail-1",
    } as HydratedImage,
} as HydratedVodVideoData;

export const MOCK_HYDRATED_VERTICAL_VIDEO_AS_DECORATION = {
    ...MOCK_HYDRATED_VERTICAL_VIDEO,
    isVideoCurationOverride: true,
};

export const MOCK_HYDRATED_SNAPSHOT_CHART_DATA = {
    snapshotDuration: "ONE DAY",
    snapshotTicker: "https://www.bloomberg.com/quote/AAPL:US",
};

export const MOCK_WARDENCLYFFE_IMAGE = {
    url: "https://assets.bwbx.io/images/users/iqjWHBFdfxIU/iDYwnS57AkrI/v0/-1x-1.jpg",
    alt: "Alt text for image",
    credit: "Credit from image",
    origWidth: 1000,
    origHeight: 1000,
    id: "BMMRIDUNIQUE",
} as WardenclyffeImage;

export const MOCK_CURATED_ITEM = {
    ...DEFAULT_WARDENCLYFFE_STORY,
    headline: { text: "Required headline", seo: null, html: null },
    summary: { text: "Required summary", seo: null, html: null },
    thumbnail: MOCK_WARDENCLYFFE_IMAGE,
    lede: MOCK_WARDENCLYFFE_IMAGE,
};

export const MOCK_CURATED_ITEM_WITH_VALID_CREDITS = {
    ...MOCK_CURATED_ITEM,
    credits: [
        {
            photoDisplayed: true,
            thumbnail: {
                ...MOCK_WARDENCLYFFE_IMAGE,
                credit: "author image credit.",
            },
        } as WardenclyffePerson,
    ],
};

export const MOCK_WARDENCLYFFE_AUDIO = {
    url: "http://www.bloomberg.com/audio.mp3",
    id: `test-avmmId-audio-1212`,
    duration: "50000",
    publishedAt: 1715899253,
    image: MOCK_WARDENCLYFFE_IMAGE,
} as WardenclyffeAudio;

export const MOCK_WARDENCLYFFE_VIDEO = {
    ...DEFAULT_WARDENCLYFFE_VIDEO,
    id: "0fb39f8-47a4-46da-92d7-c014303bdbf4",
    streams: [
        {
            url: "http://www.bloomberg.com/video.mp4",
            bitrate: null,
            codec: null,
            format: null,
            height: null,
            percent: null,
            size: null,
            type: null,
            width: null,
        },
    ],
    autoplay: false,
    thumbnail: MOCK_WARDENCLYFFE_IMAGE,
    title: "Title from associated video",
    duration: 1280821,
    aspectRatio: WardenclyffeAspectRatio.Aspect_16_9,
};

export const MOCK_WARDENCLYFFE_LOOPING_VIDEO = {
    ...MOCK_WARDENCLYFFE_VIDEO,
    autoplay: true,
    keywords: [LOOPING_VIDEO_KEYWORD],
};
export const MOCK_WARDENCLYFFE_VERTICAL_VIDEO = {
    ...MOCK_WARDENCLYFFE_VIDEO,
    aspectRatio: WardenclyffeAspectRatio.Aspect_9_16,
};

export const MOCK_CURATED_ITEM_WITH_VIDEOS = {
    ...MOCK_CURATED_ITEM,
    lede: MOCK_WARDENCLYFFE_VIDEO,
    videos: [null, MOCK_WARDENCLYFFE_VIDEO, MOCK_WARDENCLYFFE_VIDEO],
    creditCurationOverride: null,
    videoCurationOverride: null,
    _typeName: "Video",
};

export const MOCK_CURATED_ITEM_WITH_AUDIOS = {
    ...MOCK_CURATED_ITEM,
    audios: [MOCK_WARDENCLYFFE_AUDIO, MOCK_WARDENCLYFFE_AUDIO],
};

export const MOCK_WARDENCLYFFE_MAGAZINE_ISSUE = {
    coverImage: MOCK_WARDENCLYFFE_IMAGE,
    date: "March 28, 2025",
    displayDate: "March",
    id: "BWMAG1",
    modules: null,
    title: null,
};

export const MOCK_WARDENCLYFFE_NEWSLETTER = {
    __typename: "Newsletter",
    title: "Newsletter title",
    images: [
        {
            ...MOCK_WARDENCLYFFE_IMAGE,
            placement: [SQUARE_NEWSLETTER_IMAGE_PLACEMENT],
        },
    ],
} as WardenclyffeNewsletter;

/**
 * EXPORTED MOCK DATA TO BE USED IN UNIT TESTS
 *
 * Reference as MOCK_HYDRATED_MEDIA.${MediaFormat},
 * MOCK_HYDRATED_MEDIA.defaultImage, for example.
 */
export const MOCK_HYDRATED_MEDIA = {
    audioImage: MOCK_HYDRATED_IMAGE,
    authorImage: {
        ...MOCK_HYDRATED_IMAGE,
        credit: "author image credit.",
    },
    carousel: {
        hydratedCarouselItems: [
            { ...MOCK_HYDRATED_IMAGE, type: "image" },
            { ...MOCK_HYDRATED_LOOPING_VIDEO, type: "loopingVideo" },
            { ...MOCK_HYDRATED_IMAGE, type: "image" },
        ],
        numberOfItems: 3,
    } as HydratedMediaCarouselData,
    defaultImage: MOCK_HYDRATED_IMAGE,
    embeddedIframe: "https://www.bloomberg.com/embed-this-url",
    livestreamVideo: "US",
    loopingVideo: {
        video: MOCK_HYDRATED_LOOPING_VIDEO,
    },
    magazineCoverImage: MOCK_HYDRATED_IMAGE,
    newsletterImage: MOCK_HYDRATED_IMAGE,
    snapshotChart: MOCK_HYDRATED_SNAPSHOT_CHART_DATA,
    optOut: false,
    vodVideo: {
        video: MOCK_HYDRATED_VIDEO,
        image: MOCK_HYDRATED_IMAGE,
    },
    videoImage: {
        image: MOCK_HYDRATED_IMAGE,
        videoDuration: 1280821,
    },
    verticalVideo: {
        video: MOCK_HYDRATED_VERTICAL_VIDEO,
        image: MOCK_HYDRATED_IMAGE,
    },
};
